import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MdArrowBack, MdDownload, MdSensors, MdSpeed, MdAccessTime, MdDelete, MdTune, MdUploadFile } from 'react-icons/md';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine
} from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import DeleteDataModal from '../../components/admin/DeleteDataModal';
import ImportDataModal from '../../components/admin/ImportDataModal';
import SensorThresholdModal from '../../components/admin/SensorThresholdModal';
import { useLanguage } from '../../context/LanguageContext';

const CustomTooltip = ({ active, payload, label, unit }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '10px 14px', fontSize: 13
            }}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</p>
                <p style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 15 }}>
                    {payload[0].value} <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{unit}</span>
                </p>
            </div>
        );
    }
    return null;
};

export default function SensorDetailPage() {
    const { sensorUUID } = useParams();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [startDate, setStartDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [currentSensor, setCurrentSensor] = useState(null);
    const [activeRange, setActiveRange] = useState(1); // default 1 day 
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showThresholdModal, setShowThresholdModal] = useState(false);
    const itemsPerPage = 10;

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch Primary Data (includes sensor/system metadata in response)
            const dataRes = await api.get(`/sensor-data/${sensorUUID}`, {
                params: { startDate, endDate }
            });

            const rawData = dataRes.data.data || [];
            const dateLocale = language === 'tr' ? tr : undefined;
            const chartData = rawData.map(d => ({
                time: format(new Date(d.timestamp), 'yyyy/MM/dd HH:mm', { locale: dateLocale }),
                fullTime: format(new Date(d.timestamp), 'yyyy/MM/dd HH:mm:ss', { locale: dateLocale }),
                value: d.value
            }));
            setData(chartData);

            // Populate currentSensor from response payload
            if (dataRes.data.sensor) {
                const s = dataRes.data.sensor;
                setCurrentSensor({
                    ...s,
                    systemName: s.systemId?.systemName || t('common.unknownSystem')
                });
            }
        } catch (err) {
            console.error('Core analytics fetch error:', err);
            toast.error(t('sensorDetail.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [sensorUUID, startDate, endDate, language, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setQuickRange = (days) => {
        setActiveRange(days);
        const end = new Date();
        const start = subDays(end, days);
        setStartDate(format(start, 'yyyy-MM-dd'));
        setEndDate(format(end, 'yyyy-MM-dd'));
    };

    const handleCopyUUID = () => {
        navigator.clipboard.writeText(sensorUUID);
        toast.success(t('sensorDetail.idCopied'));
    };


    const handleExport = async () => {
        try {
            setExporting(true);
            const res = await api.get('/sensor-data/export/excel', {
                params: { sensorUUID, startDate, endDate },
                responseType: 'blob'
            });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sensor-${sensorUUID.substring(0, 8)}-${startDate}-${endDate}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(t('sensorDetail.excelDownloaded'));
        } catch {
            toast.error(t('sensorDetail.excelFailed'));
        } finally {
            setExporting(false);
        }
    };

    const lastValue = data.length > 0 ? data[data.length - 1].value : null;
    const minValue = data.length > 0 ? Math.min(...data.map(d => d.value)) : null;
    const maxValue = data.length > 0 ? Math.max(...data.map(d => d.value)) : null;

    return (
        <div className="page-wrapper">
            <div className="page-header">
                <div className="page-header-left">
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate(-1)} id="back-btn" style={{ marginRight: 8 }}>
                        <MdArrowBack />
                    </button>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
                            <h1 className="page-title" style={{ fontSize: 28, letterSpacing: '-0.02em', margin: 0 }}>
                                {currentSensor ? currentSensor.sensorName : (loading ? t('common.loading') : t('sensorDetail.title'))}
                            </h1>
                            {currentSensor && (
                                <span style={{
                                    fontSize: 16,
                                    fontWeight: 400,
                                    color: 'var(--text-secondary)',
                                    opacity: 0.8,
                                    borderLeft: '2px solid var(--border)',
                                    paddingLeft: 12
                                }}>
                                    {currentSensor.systemName}
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                background: 'rgba(79, 142, 247, 0.08)', border: '1px solid rgba(79, 142, 247, 0.15)',
                                padding: '8px 16px', borderRadius: 10, fontSize: 13
                            }}>
                                <span style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 14 }}>ID:</span>
                                <span style={{ fontFamily: 'monospace', color: 'var(--text-primary)', fontWeight: 600, fontSize: 14, letterSpacing: '0.02em' }}>
                                    {sensorUUID}
                                </span>
                                <button
                                    onClick={handleCopyUUID}
                                    style={{
                                        background: 'var(--primary)', color: 'white', border: 'none',
                                        cursor: 'pointer', padding: '6px 16px', marginLeft: 12, borderRadius: 6,
                                        fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                                        transition: 'all 0.2s', boxShadow: '0 2px 6px rgba(79, 142, 247, 0.3)'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 142, 247, 0.4)'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(79, 142, 247, 0.3)'; }}
                                    title={t('common.copy')}
                                >
                                    {t('common.copy')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="page-header-actions">
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowThresholdModal(true)}
                        disabled={loading || !currentSensor}
                        id="manage-threshold-btn"
                    >
                        <MdTune />
                        {t('sensorDetail.manageThreshold')}
                    </button>
                    {/* "Veri Ekle" (import data) button hidden for now — remove the wrapper to restore */}
                    {false && (
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setShowImportModal(true)}
                            disabled={loading || !currentSensor}
                            id="import-data-btn"
                        >
                            <MdUploadFile />
                            {t('sensorDetail.importData')}
                        </button>
                    )}
                    <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={loading || data.length === 0}
                        style={{ boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)' }}
                    >
                        <MdDelete />
                        {t('sensorDetail.cleanTestData')}
                    </button>
                    <button
                        className="btn btn-success btn-sm"
                        onClick={handleExport}
                        disabled={exporting || data.length === 0}
                        id="export-excel-btn"
                    >
                        {exporting ? <span className="spinner spinner-sm" /> : <MdDownload />}
                        {t('sensorDetail.exportExcel')}
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                        <MdSpeed />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{lastValue !== null && lastValue !== undefined ? `${lastValue} ${currentSensor?.unit || ''}` : '—'}</div>
                        <div className="stat-label">{t('sensorDetail.lastValue')}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(16,185,129,0.15)', color: 'var(--success)' }}>
                        ↑
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{maxValue !== null && maxValue !== undefined ? `${maxValue} ${currentSensor?.unit || ''}` : '—'}</div>
                        <div className="stat-label">{t('sensorDetail.maximum')}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--danger)' }}>
                        ↓
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{minValue !== null && minValue !== undefined ? `${minValue} ${currentSensor?.unit || ''}` : '—'}</div>
                        <div className="stat-label">{t('sensorDetail.minimum')}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--warning)' }}>
                        <MdAccessTime />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{data.length}</div>
                        <div className="stat-label">{t('sensorDetail.dataCount')}</div>
                    </div>
                </div>
                {currentSensor && ((currentSensor.minThreshold !== null && currentSensor.minThreshold !== undefined) || (currentSensor.maxThreshold !== null && currentSensor.maxThreshold !== undefined)) && (
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'rgba(139,92,246,0.15)', color: 'var(--secondary)' }}>
                            <MdTune />
                        </div>
                        <div className="stat-info">
                            <div className="stat-value" style={{ fontSize: 16 }}>
                                {currentSensor.minThreshold !== null && currentSensor.minThreshold !== undefined ? `${currentSensor.minThreshold} ${currentSensor.unit}` : '—'}
                                {' / '}
                                {currentSensor.maxThreshold !== null && currentSensor.maxThreshold !== undefined ? `${currentSensor.maxThreshold} ${currentSensor.unit}` : '—'}
                            </div>
                            <div className="stat-label">{t('sensorDetail.thresholdTitle')} ({t('sensorDetail.minThresholdLabel')} / {t('sensorDetail.maxThresholdLabel')})</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Date filter */}
            <div className="filter-bar">
                <span className="filter-label">📅 {t('sensorDetail.dateRange')}:</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('sensorDetail.startDate')}</span>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} id="start-date" />
                </label>
                <span style={{ color: 'var(--text-muted)', alignSelf: 'flex-end', paddingBottom: 6 }}>→</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('sensorDetail.endDate')}</span>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} id="end-date" />
                </label>
                <button className="btn btn-secondary btn-sm" onClick={fetchData} id="filter-btn" style={{ alignSelf: 'flex-end' }}>{t('common.filter')}</button>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignSelf: 'flex-end' }}>
                    <button className={`btn btn-sm ${activeRange === 1 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange(1)}>{t('sensorDetail.daily')}</button>
                    <button className={`btn btn-sm ${activeRange === 7 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange(7)}>{t('sensorDetail.weekly')}</button>
                    <button className={`btn btn-sm ${activeRange === 30 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange(30)}>{t('sensorDetail.monthly')}</button>
                </div>
            </div>

            {/* Chart */}
            <motion.div className="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="card-header">
                    <div className="card-title">
                        <div className="card-title-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                            <MdSensors />
                        </div>
                        {t('sensorDetail.timeSeriesChart')}
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="loading-container"><div className="spinner"></div></div>
                    ) : data.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">📊</div>
                            <div className="empty-state-title">{t('sensorDetail.noDataInRange')}</div>
                            <div className="empty-state-desc">{t('sensorDetail.selectDifferentRange')}</div>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={350}>
                            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4f8ef7" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                <XAxis
                                    dataKey="time"
                                    tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                    tickLine={false}
                                    axisLine={false}
                                    interval="preserveStartEnd"
                                />
                                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip unit={currentSensor?.unit} />} />
                                {currentSensor?.minThreshold !== null && currentSensor?.minThreshold !== undefined && (
                                    <ReferenceLine y={currentSensor.minThreshold} stroke="#f59e0b" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: t('sensorDetail.minThresholdLabel'), position: 'insideBottomLeft', fill: '#f59e0b', fontSize: 11 }} />
                                )}
                                {currentSensor?.maxThreshold !== null && currentSensor?.maxThreshold !== undefined && (
                                    <ReferenceLine y={currentSensor.maxThreshold} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: t('sensorDetail.maxThresholdLabel'), position: 'insideTopLeft', fill: '#ef4444', fontSize: 11 }} />
                                )}
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#4f8ef7"
                                    strokeWidth={2.5}
                                    fill="url(#colorVal)"
                                    dot={data.length < 30 ? { fill: '#4f8ef7', r: 3 } : false}
                                    activeDot={{ r: 5, fill: '#4f8ef7', stroke: 'white', strokeWidth: 2 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </motion.div>

            {/* Data Table */}
            {data.length > 0 && (
                <motion.div className="card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                    style={{ marginTop: 20 }}>
                    <div className="card-header">
                        <div className="card-title">{t('sensorDetail.recentRecords')}</div>
                        <span className="badge badge-primary">{data.length} {t('common.records')}</span>
                    </div>
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr><th>{t('sensorDetail.dateTime')}</th><th>{t('common.value')}</th></tr>
                            </thead>
                            <tbody>
                                {[...data].reverse()
                                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                    .map((d, i) => (
                                        <tr key={i}>
                                            <td style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 500 }}>{d.fullTime || d.time}</td>
                                            <td>
                                                <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 20 }}>{d.value}</span>
                                                <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 6 }}>{currentSensor?.unit}</span>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {data.length > itemsPerPage && (
                        <div className="pagination" style={{ display: 'flex', justifyContent: 'center', padding: '15px', gap: '8px', borderTop: '1px solid var(--border)' }}>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                {t('common.back')}
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: 'var(--text-muted)', padding: '0 10px' }}>
                                {t('common.page')} {currentPage} / {Math.ceil(data.length / itemsPerPage)}
                            </div>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCurrentPage(p => Math.min(Math.ceil(data.length / itemsPerPage), p + 1))}
                                disabled={currentPage === Math.ceil(data.length / itemsPerPage)}
                            >
                                {t('common.forward')}
                            </button>
                        </div>
                    )}
                </motion.div>
            )}

            {showDeleteModal && (
                <DeleteDataModal
                    sensorUUID={sensorUUID}
                    sensorName={currentSensor?.sensorName}
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={() => {
                        setShowDeleteModal(false);
                        fetchData();
                    }}
                />
            )}

            {showImportModal && currentSensor && (
                <ImportDataModal
                    sensorUUID={sensorUUID}
                    sensorName={currentSensor.sensorName}
                    unit={currentSensor.unit}
                    onClose={() => setShowImportModal(false)}
                    onSuccess={() => {
                        setShowImportModal(false);
                        fetchData();
                    }}
                />
            )}

            {showThresholdModal && currentSensor && (
                <SensorThresholdModal
                    sensor={currentSensor}
                    onClose={() => setShowThresholdModal(false)}
                    onSuccess={() => {
                        setShowThresholdModal(false);
                        fetchData();
                    }}
                />
            )}
        </div>
    );
}
